import type { FocusEvent, KeyboardEvent } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import { peekTppSnapshot } from '../../../services/systems/transit-pass-pool/tppLocalDb';

export const TPP_MODAL_CONTENT_CLASS = 'tpp-modal bg-white border-0 shadow';

/** Enter / Space でクリック相当（行選択・ソート見出しなど） */
export function tppActivateKeyHandler(activate: () => void) {
  return (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activate();
    }
  };
}

/** カード選択後に履歴パネルへ寄せる（一覧クリック・概況フォーカス共通） */
export function scrollTppCardHistoryIntoView() {
  requestAnimationFrame(() => {
    document
      .getElementById('tpp-card-history')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
}

/** 操作モーダルの日付初期値（ローカル日付の ISO） */
export function tppTodayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/** 必須ラベルの赤い *（読み上げはスキップ） */
export function TppRequiredMark() {
  return (
    <span className="text-danger ms-1" aria-hidden>
      *
    </span>
  );
}

/** 任意項目の注記（必須マークと対） */
export function TppOptionalMark() {
  const { t } = useTranslation();
  return (
    <span className="text-muted small fw-normal ms-1">（{t('tpp.optional')}）</span>
  );
}

/** -0 を 0 にしてから toLocaleString（残高・履歴金額の共通） */
export function formatTppBalanceAmount(n: number, fractionDigits = 0): string {
  const v = Object.is(n, -0) ? 0 : n;
  const digits = Math.min(18, Math.max(0, Math.trunc(fractionDigits)));
  return v.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function formatTppBalanceLabel(
  n: number,
  prefix: string,
  suffix: string,
  fractionDigits = 0
): string {
  return `${prefix}${formatTppBalanceAmount(n, fractionDigits)}${suffix}`;
}

/** 編集フォーム用。チャージは UI 上は正数で扱う */
export function tppAmountStrForEdit(
  eventKind: string,
  amountNumber: number | null | undefined
): string {
  if (amountNumber == null) return '';
  return eventKind === 'charge' ? String(Math.abs(amountNumber)) : String(amountNumber);
}

export function nextTppDisplayNumber(cards: Array<{ displayNumber: number }>): number {
  const max = cards.reduce((m, c) => Math.max(m, c.displayNumber), 0);
  return max + 1;
}

/** プール内の全カードから次の表示番号（ページ／検索フィルタに依存しない） */
export function nextTppDisplayNumberForPool(poolKey: string): number {
  const snap = peekTppSnapshot();
  if (!snap) return 1;
  return nextTppDisplayNumber(snap.cards.filter((c) => c.poolKey === poolKey));
}

export function TppBtnSpinner({ show }: { show: boolean }) {
  if (!show) return null;
  return <Spinner animation="border" size="sm" className="me-2" role="status" aria-hidden />;
}

/** 金額入力欄 → 非負の数値。不正なら null */
export function parseTppAmountInput(raw: string): number | null {
  const s = normalizeFullWidthNumerics(raw).trim().replace(/,/g, '');
  // Number('') === 0 なので空は未入力（未使用は明示の 0 のみ）
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** 金額欄が送信可能か（0 円は可、空・不正は不可） */
export function isTppAmountInputReady(raw: string): boolean {
  return parseTppAmountInput(raw) != null;
}

/** チャージ等：0 超のみ */
export function isTppPositiveAmountInputReady(raw: string): boolean {
  const n = parseTppAmountInput(raw);
  return n != null && n > 0;
}

export function formatTppLocalDateTime(
  iso: string | null | undefined,
  locale?: string
): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale || undefined);
}

/** 返却（減算）／チャージ（加算）の残高見込み */
export function TppBalanceDeltaHint({
  amountRaw,
  balanceNumber,
  moneyLabel,
  mode,
  afterTemplate,
  negativeHint,
}: {
  amountRaw: string;
  balanceNumber: number;
  moneyLabel: (n: number) => string;
  mode: 'add' | 'subtract';
  afterTemplate: (balance: string) => string;
  negativeHint?: string;
}) {
  const n = parseTppAmountInput(amountRaw);
  if (n == null || amountRaw.trim() === '') return null;
  const after = mode === 'add' ? balanceNumber + n : balanceNumber - n;
  return (
    <Form.Text className={`d-block ${after < 0 ? 'text-danger' : 'text-body-secondary'}`}>
      {afterTemplate(moneyLabel(after))}
      {after < 0 && negativeHint ? ` ${negativeHint}` : ''}
    </Form.Text>
  );
}

/** 日付入力（ブラウザ標準の date ピッカーに任せる） */
export function TppDateField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Form.Group className="mb-2">
      <Form.Label htmlFor={id}>{label}</Form.Label>
      <Form.Control
        id={id}
        type="date"
        lang="ja"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </Form.Group>
  );
}

/** 金額欄フォーカス時に全選択（既定値の上書きを速くする） */
export function selectTppInputOnFocus(e: FocusEvent<HTMLInputElement>) {
  e.currentTarget.select();
}

/** チャージでよく使う額（空欄＋ワンタップ） */
export const TPP_CHARGE_AMOUNT_PRESETS = [1000, 2000, 3000, 5000, 10000] as const;

/** 数値のみのチャージ額プリセット（円は入力欄ラベル側） */
export function TppChargeAmountPresets({
  amount,
  onPick,
  ariaLabel,
}: {
  amount: string;
  onPick: (n: number) => void;
  ariaLabel: string;
}) {
  return (
    <div className="tpp-charge-presets mb-2" role="group" aria-label={ariaLabel}>
      {TPP_CHARGE_AMOUNT_PRESETS.map((n) => {
        const selected = amount === String(n);
        return (
          <Button
            key={n}
            type="button"
            size="sm"
            variant={selected ? 'success' : 'outline-secondary'}
            onClick={() => onPick(n)}
          >
            {n.toLocaleString()}
          </Button>
        );
      })}
    </div>
  );
}
